import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../supabaseClient';
import { Plus } from 'lucide-react';
import {
  IMAGE_BUCKET,
  copyStorageObject,
  getStorageFileName,
  resolveMonthlyStoragePath,
} from './imageStoragePaths';

const BUCKET = IMAGE_BUCKET;

const formatYearMonthLabel = (ym) => {
  if (!ym || ym.length !== 6) return ym || '-';
  return `${ym.slice(0, 4)}년 ${ym.slice(4, 6)}월`;
};

const monthInputToYm = (value) => (value ? value.replace(/-/g, '') : '');

const ymToMonthInput = (ym) => {
  if (!ym || ym.length !== 6) return '';
  return `${ym.slice(0, 4)}-${ym.slice(4, 6)}`;
};

const addOneMonthYm = (ym) => {
  const year = parseInt(ym.slice(0, 4), 10);
  const month = parseInt(ym.slice(4, 6), 10);
  const date = new Date(year, month - 1, 1);
  date.setMonth(date.getMonth() + 1);
  return `${date.getFullYear()}${String(date.getMonth() + 1).padStart(2, '0')}`;
};

const getDefaultNextYm = (monthList) => {
  if (monthList.length > 0) return addOneMonthYm(monthList[0].year_month);
  const now = new Date();
  return `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}`;
};

const getMonthPeriodYmd = (ym) => {
  if (!ym || ym.length !== 6) return { start_ymd: null, end_ymd: null };
  const year = parseInt(ym.slice(0, 4), 10);
  const month = parseInt(ym.slice(4, 6), 10);
  const monthStr = String(month).padStart(2, '0');
  const start_ymd = `${year}${monthStr}01`;
  const lastDay = new Date(year, month, 0).getDate();
  const end_ymd = `${year}${monthStr}${String(lastDay).padStart(2, '0')}`;
  return { start_ymd, end_ymd };
};

/** DB text(YYYYMMDD 또는 YYYY-MM-DD) → YYYY-MM-DD 표시 */
const formatYmdDisplay = (value) => {
  if (!value) return '-';
  const digits = String(value).replace(/\D/g, '');
  if (digits.length === 8) {
    return `${digits.slice(0, 4)}-${digits.slice(4, 6)}-${digits.slice(6, 8)}`;
  }
  if (/^\d{4}-\d{2}-\d{2}$/.test(String(value).trim())) {
    return String(value).trim();
  }
  return value;
};

const Thumbnail = ({ storagePath }) => {
  const [failed, setFailed] = useState(false);
  if (!storagePath || failed) {
    return (
      <div
        className="border rounded d-flex align-items-center justify-content-center bg-light text-muted small"
        style={{ width: 80, height: 56 }}
      >
        없음
      </div>
    );
  }
  const { data } = supabase.storage.from(BUCKET).getPublicUrl(storagePath);
  return (
    <div className="border rounded overflow-hidden bg-light" style={{ width: 80, height: 56 }}>
      <img
        src={data.publicUrl}
        alt="대표"
        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
        onError={() => setFailed(true)}
      />
    </div>
  );
};

const ImageManagementMonthList = () => {
  const navigate = useNavigate();
  const [months, setMonths] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showNewModal, setShowNewModal] = useState(false);
  const [newMonthInput, setNewMonthInput] = useState('');
  const [activateModal, setActivateModal] = useState(null);
  const [activating, setActivating] = useState(false);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    fetchMonths();
  }, []);

  const fetchMonths = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: fetchError } = await supabase
        .from('page_image_management')
        .select('id, year_month, storage_path, position_name, is_active, start_ymd, end_ymd')
        .not('year_month', 'is', null)
        .order('year_month', { ascending: false });

      if (fetchError) throw fetchError;

      const grouped = {};
      (data || []).forEach((row) => {
        const ym = row.year_month;
        if (!grouped[ym]) {
          grouped[ym] = {
            year_month: ym,
            is_active: false,
            representative_path: null,
            item_count: 0,
            start_ymd: '',
            end_ymd: '',
          };
        }
        grouped[ym].item_count += 1;
        if (row.is_active === 'Y') grouped[ym].is_active = true;
        if (row.start_ymd && !grouped[ym].start_ymd) grouped[ym].start_ymd = row.start_ymd;
        if (row.end_ymd && !grouped[ym].end_ymd) grouped[ym].end_ymd = row.end_ymd;
        if (row.position_name === 'QT Card Background') {
          grouped[ym].representative_path = row.storage_path;
        } else if (!grouped[ym].representative_path) {
          grouped[ym].representative_path = row.storage_path;
        }
      });

      const list = Object.values(grouped).sort((a, b) =>
        b.year_month.localeCompare(a.year_month)
      );
      setMonths(list);
    } catch (e) {
      setError(e.message || '목록 조회 실패');
      setMonths([]);
    } finally {
      setLoading(false);
    }
  };

  const openNewModal = () => {
    setNewMonthInput(ymToMonthInput(getDefaultNextYm(months)));
    setShowNewModal(true);
  };

  const copyMonthFromLatest = async (sourceYm, targetYm) => {
    const { data: sourceRows, error: fetchError } = await supabase
      .from('page_image_management')
      .select(
        'page_path, position_name, storage_path, file_name, description, sort_no, version'
      )
      .eq('year_month', sourceYm)
      .order('sort_no', { ascending: true, nullsFirst: false });

    if (fetchError) throw fetchError;
    if (!sourceRows?.length) return 0;

    const { start_ymd, end_ymd } = getMonthPeriodYmd(targetYm);

    const inserts = [];
    for (const row of sourceRows) {
      const storage_path = resolveMonthlyStoragePath(row.storage_path, targetYm);

      if (row.storage_path !== storage_path) {
        try {
          await copyStorageObject(supabase, row.storage_path, storage_path);
        } catch (copyError) {
          console.warn('Storage copy skipped:', row.storage_path, '->', storage_path, copyError.message);
        }
      }

      inserts.push({
        page_path: row.page_path,
        position_name: row.position_name,
        storage_path,
        file_name: getStorageFileName(storage_path) || row.file_name || null,
        description: row.description,
        year_month: targetYm,
        is_active: 'N',
        start_ymd,
        end_ymd,
        sort_no: row.sort_no,
        version: row.version ?? 1,
      });
    }

    const { error: insertError } = await supabase.from('page_image_management').insert(inserts);
    if (insertError) throw insertError;
    return inserts.length;
  };

  const handleNewSubmit = async (e) => {
    e.preventDefault();
    const targetYm = monthInputToYm(newMonthInput);
    if (!targetYm || targetYm.length !== 6) {
      alert('년월을 선택해주세요.');
      return;
    }

    const latestYm = months.length > 0 ? months[0].year_month : null;

    setCreating(true);
    try {
      const { data: existingRows, error: existingError } = await supabase
        .from('page_image_management')
        .select('id')
        .eq('year_month', targetYm)
        .limit(1);
      if (existingError) throw existingError;

      if (existingRows?.length > 0) {
        setShowNewModal(false);
        navigate(`/image-management/detail?ym=${targetYm}`);
        return;
      }

      if (latestYm) {
        await copyMonthFromLatest(latestYm, targetYm);
      }

      setShowNewModal(false);
      navigate(`/image-management/detail?ym=${targetYm}`);
    } catch (err) {
      alert(err.message || '신규 월 생성에 실패했습니다.');
    } finally {
      setCreating(false);
    }
  };

  const openActivateConfirm = (month) => {
    setActivateModal({ ...month, targetActive: !month.is_active });
  };

  const confirmActivate = async () => {
    // console.log('confirmActivate', activateModal);
    if (!activateModal) return;
    setActivating(true);
    try {
      const nextActive = activateModal.targetActive ?? !activateModal.is_active;
      // console.log('confirmActivate', activateModal.year_month, nextActive);
      const { error: setError } = await supabase
        .from('page_image_management')
        .update({ is_active: nextActive ? 'Y' : 'N' })
        .eq('year_month', activateModal.year_month);
      if (setError) throw setError;

      setActivateModal(null);
      await fetchMonths();
    } catch (e) {
      alert(e.message || '활성화 변경에 실패했습니다.');
    } finally {
      setActivating(false);
    }
  };

  if (loading) {
    return (
      <div className="card shadow mb-4">
        <div className="card-body">로딩 중...</div>
      </div>
    );
  }

  return (
    <>
      <div className="card shadow mb-4">
        <div className="card-header py-3 d-flex justify-content-between align-items-center">
          <h6 className="m-0 font-weight-bold text-primary">월별 이미지 관리</h6>
          <button type="button" className="btn btn-success btn-sm" onClick={openNewModal}>
            <Plus size={16} className="mr-1" />
            신규생성
          </button>
        </div>
        <div className="card-body">
          {error && <div className="alert alert-danger">{error}</div>}
          <div className="table-responsive">
            <table className="table table-bordered">
              <thead className="thead-light">
                <tr>
                  <th style={{ width: 140 }}>년월</th>
                  <th style={{ width: 120 }}>시작일</th>
                  <th style={{ width: 120 }}>종료일</th>
                  <th style={{ width: 120 }}>대표이미지</th>
                  <th style={{ width: 100 }}>활성화</th>
                  <th style={{ width: 120 }}>수정</th>
                </tr>
              </thead>
              <tbody>
                {months.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="text-center text-muted">
                      등록된 월별 이미지가 없습니다. 신규생성으로 추가하세요.
                    </td>
                  </tr>
                ) : (
                  months.map((month) => (
                    <tr key={month.year_month}>
                      <td className="align-middle font-weight-bold">
                        {formatYearMonthLabel(month.year_month)}
                        <div className="small text-muted">{month.item_count}개 항목</div>
                      </td>
                      <td className="align-middle">{formatYmdDisplay(month.start_ymd)}</td>
                      <td className="align-middle">{formatYmdDisplay(month.end_ymd)}</td>
                      <td className="align-middle">
                        <Thumbnail storagePath={month.representative_path} />
                      </td>
                      <td className="align-middle text-center">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          style={{ position: 'static', margin: 0, transform: 'scale(1.25)' }}
                          checked={month.is_active}
                          onChange={() => openActivateConfirm(month)}
                          disabled={activating}
                          aria-label={`${formatYearMonthLabel(month.year_month)} 활성화`}
                        />
                      </td>
                      <td className="align-middle">
                        <button
                          type="button"
                          className="btn btn-sm btn-primary"
                          onClick={() => navigate(`/image-management/detail?ym=${month.year_month}`)}
                        >
                          수정
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {showNewModal && (
        <div className="modal show d-block" tabIndex={-1} style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">월별 이미지 신규생성</h5>
                <button type="button" className="close" onClick={() => setShowNewModal(false)} aria-label="닫기">
                  <span aria-hidden="true">&times;</span>
                </button>
              </div>
              <form onSubmit={handleNewSubmit}>
                <div className="modal-body">
                  <div className="form-group mb-0">
                    <label>년월</label>
                    <input
                      type="month"
                      className="form-control"
                      value={newMonthInput}
                      onChange={(e) => setNewMonthInput(e.target.value)}
                      required
                    />
                    {months.length > 0 && (
                      <small className="form-text text-muted">
                        기준: {formatYearMonthLabel(months[0].year_month)} → 복사 대상{' '}
                        {formatYearMonthLabel(getDefaultNextYm(months))}
                      </small>
                    )}
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setShowNewModal(false)}
                    disabled={creating}
                  >
                    취소
                  </button>
                  <button type="submit" className="btn btn-primary" disabled={creating}>
                    {creating ? '생성 중...' : '이동'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {activateModal && (
        <div className="modal show d-block" tabIndex={-1} style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">활성화 확인</h5>
                <button
                  type="button"
                  className="close"
                  onClick={() => setActivateModal(null)}
                  disabled={activating}
                  aria-label="닫기"
                >
                  <span aria-hidden="true">&times;</span>
                </button>
              </div>
              <div className="modal-body">
                <p className="mb-0">
                  <strong>{formatYearMonthLabel(activateModal.year_month)}</strong>
                  을(를) {activateModal.targetActive ? '활성화' : '비활성화'}하시겠습니까?
                </p>
                <p className="text-muted small mt-2 mb-0">
                  여러 년월을 동시에 활성화할 수 있습니다. 다른 년월의 활성화 상태는 변경되지 않습니다.
                </p>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setActivateModal(null)}
                  disabled={activating}
                >
                  취소
                </button>
                <button type="button" className="btn btn-primary" onClick={confirmActivate} disabled={activating}>
                  {activating ? '처리 중...' : '확인'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ImageManagementMonthList;
